import { z } from 'zod';
import { emptyToUndefined } from './utils.js';
/*
 * Payment options are validated from DB/JSON data where absent keys arrive as undefined.
 * z.string().optional() as the base accepts both string and undefined inputs so absent
 * optional fields (label, reference) do not cause SafeParse to fail and silently drop
 * the entire payment option during template rendering.
 */
const optionalStr = z
    .string()
    .optional()
    .transform(emptyToUndefined)
    .pipe(z.string().optional());
/** The supported payment method types. */
export const PaymentOptionTypeSchema = z.enum([
    'BANK_TRANSFER',
    'PAYPAL',
    'STRIPE',
    'CREDIT_CARD',
    'CUSTOM',
]);
/** Payment option for bank transfers — requires BSB, account number, and account name. */
export const BankTransferOptionSchema = z.object({
    type: z.literal('BANK_TRANSFER'),
    isDefault: z.boolean(),
    label: optionalStr,
    bsb: z.string().min(6),
    accountNumber: z.string().min(8),
    accountName: z.string().min(1),
    reference: optionalStr,
});
/** Payment option for PayPal — requires a verified email address. */
export const PaypalOptionSchema = z.object({
    type: z.literal('PAYPAL'),
    isDefault: z.boolean(),
    label: optionalStr,
    email: z.string().transform(emptyToUndefined).pipe(z.string().email()),
});
/** Payment option for Stripe — requires a payment link URL. */
export const StripeOptionSchema = z.object({
    type: z.literal('STRIPE'),
    isDefault: z.boolean(),
    label: optionalStr,
    paymentLink: z.string().transform(emptyToUndefined).pipe(z.string().url()),
});
/** Payment option for manual credit card instructions. */
export const CreditCardOptionSchema = z.object({
    type: z.literal('CREDIT_CARD'),
    isDefault: z.boolean(),
    label: optionalStr,
    instructions: z.string().min(1),
});
/**
 * Fully custom payment option. Label is required (unlike other types where it is
 * optional) because it is the only identifier shown to clients.
 */
export const CustomOptionSchema = z.object({
    type: z.literal('CUSTOM'),
    isDefault: z.boolean(),
    label: z.string().min(1, { error: 'Label is required for custom payment methods' }),
    instructions: z.string().min(1),
});
/** Discriminated union of all supported payment option variants, keyed on type. */
export const PaymentOptionSchema = z.discriminatedUnion('type', [
    BankTransferOptionSchema,
    PaypalOptionSchema,
    StripeOptionSchema,
    CreditCardOptionSchema,
    CustomOptionSchema,
]);
/**
 * Fallback used when an organisation hasn't configured payment terms yet — mirrors the
 * Prisma column default on `Organization.paymentTermsDays`.
 */
export const DEFAULT_PAYMENT_TERMS_DAYS = 30;
/**
 * Quick-select presets shown in the "Default payment terms" selector — Net
 * 15/30/45/60/90.
 */
export const PAYMENT_TERMS_DAY_PRESETS = [15, 30, 45, 60, 90];
//# sourceMappingURL=payments.js.map